import { useState, useCallback } from 'react';
import { useMusicKit } from './useMusicKit';
import { usePreviewPlayer } from './usePreviewPlayer';
import { useYouTubePlayer } from './useYouTubePlayer';
import { getYouTubeVideoId } from '../data/youtube-mappings';
import type { GeneratedPlaylist, ResolvedTrack, PlaybackState, Platform } from '../types';

interface UseActivePlayerOptions {
  platform: Platform;
  youtubeContainerId: string;
  isOnPlayerScreen: boolean;
}

interface UseActivePlayerReturn {
  // Unified playback controls
  play: () => Promise<void>;
  pause: () => void;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  skipToTrack: (index: number) => Promise<void>;

  // Derived state
  playbackState: PlaybackState;
  isPlaying: boolean;
  currentTrackIndex: number;
  currentArtworkUrl: string | null;

  // Resolution
  resolveAndStart: (playlist: GeneratedPlaylist) => Promise<ResolvedTrack[]>;
  isResolving: boolean;
  resolutionError: string | null;

  // Platform-specific pass-throughs
  youtubeError: { id: number; message: string; videoId?: string | null } | null;
  clearYouTubeError: () => void;
  previewTracksPlayed: number;

  // Reset helpers
  stopAndReset: () => void;
  pauseActive: () => void;
  pauseAndReset: () => void;

  // MusicKit re-export for auth/save flows
  musicKit: ReturnType<typeof useMusicKit>;

  // Platform booleans
  isPreviewSelection: boolean;
  isAppleMusicSelection: boolean;
  isYouTubeSelection: boolean;
  isPreviewUi: boolean;
}

function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function useActivePlayer({
  platform,
  youtubeContainerId,
  isOnPlayerScreen,
}: UseActivePlayerOptions): UseActivePlayerReturn {
  const musicKit = useMusicKit();
  const previewPlayer = usePreviewPlayer();
  const youtubePlayer = useYouTubePlayer({
    containerId: youtubeContainerId,
    isVisible: isOnPlayerScreen && platform === 'youtube',
  });

  const [isResolving, setIsResolving] = useState(false);
  const [resolutionError, setResolutionError] = useState<string | null>(null);

  const isPreviewSelection = platform === 'preview';
  const isAppleMusicSelection = platform === 'apple-music';
  const isYouTubeSelection = platform === 'youtube';
  const isPreviewUi = isPreviewSelection || (isAppleMusicSelection && musicKit.isPreviewMode);

  // Derived state based on active platform
  const playbackState = isYouTubeSelection
    ? youtubePlayer.playbackState
    : isPreviewSelection
      ? previewPlayer.playbackState
      : musicKit.playbackState;

  const isPlaying = isYouTubeSelection
    ? youtubePlayer.isPlaying
    : isPreviewSelection
      ? previewPlayer.isPlaying
      : musicKit.isPlaying;

  const currentTrackIndex = isYouTubeSelection
    ? youtubePlayer.currentTrackIndex
    : isPreviewSelection
      ? previewPlayer.currentTrackIndex
      : musicKit.currentTrackIndex;

  const currentArtworkUrl = isYouTubeSelection
    ? youtubePlayer.currentArtworkUrl
    : isPreviewSelection
      ? previewPlayer.currentArtworkUrl
      : musicKit.currentArtworkUrl;

  // Unified playback controls
  const play = useCallback(async () => {
    if (isYouTubeSelection) {
      await youtubePlayer.play();
    } else if (isPreviewSelection) {
      await previewPlayer.play();
    } else {
      await musicKit.play();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  const pause = useCallback(() => {
    if (isYouTubeSelection) {
      youtubePlayer.pause();
    } else if (isPreviewSelection) {
      previewPlayer.pause();
    } else {
      musicKit.pause();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  const skipToNext = useCallback(async () => {
    if (isYouTubeSelection) {
      await youtubePlayer.skipToNext();
    } else if (isPreviewSelection) {
      await previewPlayer.skipToNext();
    } else {
      await musicKit.skipToNext();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  const skipToPrevious = useCallback(async () => {
    if (isYouTubeSelection) {
      await youtubePlayer.skipToPrevious();
    } else if (isPreviewSelection) {
      await previewPlayer.skipToPrevious();
    } else {
      await musicKit.skipToPrevious();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  const skipToTrack = useCallback(async (index: number) => {
    if (isYouTubeSelection) {
      await youtubePlayer.skipToTrack(index);
    } else if (isPreviewSelection) {
      await previewPlayer.skipToTrack(index);
    } else {
      await musicKit.skipToTrack(index);
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  // Resolve and start playback for the active platform
  const resolveAndStart = useCallback(async (playlist: GeneratedPlaylist): Promise<ResolvedTrack[]> => {
    setIsResolving(true);
    setResolutionError(null);

    try {
      let resolved: ResolvedTrack[];

      if (isYouTubeSelection) {
        // YouTube: local resolution via video ID mapping
        resolved = playlist.tracks.reduce<ResolvedTrack[]>((acc, track) => {
          const videoId = getYouTubeVideoId(track.isrc);
          if (!videoId) return acc;
          acc.push({
            isrc: track.isrc,
            catalogId: videoId,
            youtubeVideoId: videoId,
            title: track.title,
            artist: track.artist,
            durationSeconds: track.durationSeconds,
            phase: track.phase,
            artworkUrl: getYouTubeThumbnailUrl(videoId),
          });
          return acc;
        }, []);

        if (resolved.length === 0) {
          throw new Error('No YouTube videos available for this playlist');
        }

        youtubePlayer.setQueue(resolved);
        setIsResolving(false);
        await youtubePlayer.play();
      } else if (isPreviewSelection) {
        // Preview: resolve via preview API
        const isrcs = playlist.tracks.map(t => t.isrc);
        const resolution = await previewPlayer.resolveIsrcsPreview(isrcs);

        resolved = playlist.tracks
          .filter(t => resolution.resolved[t.isrc])
          .map(t => ({
            isrc: t.isrc,
            catalogId: resolution.resolved[t.isrc].catalogId,
            title: t.title,
            artist: t.artist,
            durationSeconds: t.durationSeconds,
            phase: t.phase,
            previewUrl: resolution.resolved[t.isrc].previewUrl,
            artworkUrl: resolution.resolved[t.isrc].artworkUrl,
          }));

        if (resolved.length === 0) {
          throw new Error('No tracks could be resolved');
        }

        const tracksWithPreviews = resolved.filter(t => t.previewUrl);
        if (tracksWithPreviews.length === 0) {
          throw new Error('No preview audio available for these tracks');
        }

        previewPlayer.setQueue(resolved);
        setIsResolving(false);
        await previewPlayer.play();
      } else {
        // Apple Music: resolve via authenticated API
        const isrcs = playlist.tracks.map(t => t.isrc);
        const resolution = await musicKit.resolveIsrcs(isrcs);

        resolved = playlist.tracks
          .filter(t => resolution.resolved[t.isrc])
          .map(t => ({
            isrc: t.isrc,
            catalogId: resolution.resolved[t.isrc],
            title: t.title,
            artist: t.artist,
            durationSeconds: t.durationSeconds,
            phase: t.phase,
          }));

        if (resolved.length === 0) {
          throw new Error('No tracks could be resolved');
        }

        const catalogIds = resolved.map(t => t.catalogId);
        await musicKit.setQueue(catalogIds);
        setIsResolving(false);
        await musicKit.play();
      }

      return resolved;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tracks';
      setResolutionError(message);
      setIsResolving(false);
      return [];
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  // Stop active player and reset its state
  const stopAndReset = useCallback(() => {
    if (isYouTubeSelection) {
      youtubePlayer.reset();
    } else if (isPreviewSelection) {
      previewPlayer.pause();
      previewPlayer.reset();
    } else {
      musicKit.pause();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  // Just pause the active player
  const pauseActive = useCallback(() => {
    if (isYouTubeSelection) {
      youtubePlayer.pause();
    } else if (isPreviewSelection) {
      previewPlayer.pause();
    } else {
      musicKit.pause();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  // Pause and reset (for logo click / disconnect where preview needs both)
  const pauseAndReset = useCallback(() => {
    if (isYouTubeSelection) {
      youtubePlayer.reset();
    } else if (isPreviewSelection) {
      previewPlayer.pause();
      previewPlayer.reset();
    } else {
      musicKit.pause();
    }
  }, [isYouTubeSelection, isPreviewSelection, youtubePlayer, previewPlayer, musicKit]);

  return {
    play,
    pause,
    skipToNext,
    skipToPrevious,
    skipToTrack,
    playbackState,
    isPlaying,
    currentTrackIndex,
    currentArtworkUrl,
    resolveAndStart,
    isResolving,
    resolutionError,
    youtubeError: youtubePlayer.error,
    clearYouTubeError: youtubePlayer.clearError,
    previewTracksPlayed: previewPlayer.previewTracksPlayed,
    stopAndReset,
    pauseActive,
    pauseAndReset,
    musicKit,
    isPreviewSelection,
    isAppleMusicSelection,
    isYouTubeSelection,
    isPreviewUi,
  };
}
